'use client';

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ProductCard } from "@/components/product/product-card";
import { useQuery } from "@tanstack/react-query";
import { HomePageConfig } from "@/lib/home-config-types";
import { ArrowLeftIcon } from "@/components/icons/arrow-left-icon";
import { ArrowRightIcon } from "@/components/icons/arrow-right-icon";
import { ChevronRightIcon } from "@/components/icons/chevron-right-icon";
import { SortIcon } from "@/components/icons/sort-icon";
import { FilterFunnelIcon } from "@/components/icons/filter-funnel-icon";
import { ChevronDownIcon } from "@/components/icons/chevron-down-icon";

// Search Category Page Component - Uses exact category page structure
function SearchCategoryPage({ searchQuery }: { searchQuery: string }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sidebarSticky, setSidebarSticky] = useState(true);

  const [imageBg, setImageBg] = useState<'white' | 'black'>('white');
  const [priceSliderRange, setPriceSliderRange] = useState<[number, number]>([0, 20000]);
  const [tempPriceSliderRange, setTempPriceSliderRange] = useState<[number, number]>([0, 20000]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showAllOptions, setShowAllOptions] = useState<Record<string, boolean>>({});

  const itemsPerPage = 24;

  const [dynamicFilters, setDynamicFilters] = useState({
    priceRange: [] as string[],
    variantOptions: {} as Record<string, string[]>,
    productProperties: {} as Record<string, string[]>,
    productTypes: [] as string[]
  });

  const [tempFilters, setTempFilters] = useState({
    priceRange: [] as string[],
    variantOptions: {} as Record<string, string[]>,
    productProperties: {} as Record<string, string[]>,
    productTypes: [] as string[]
  });

  // Smart sticky positioning for sidebar
  useEffect(() => {
    const handleScroll = () => {
      const footerElement = document.querySelector('footer');
      if (!footerElement) return;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const distanceFromBottom = documentHeight - scrollY - windowHeight;
      setSidebarSticky(distanceFromBottom > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const { data: variantOptions } = useQuery({
    queryKey: ["/api/variant-options", searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      const response = await fetch(`/api/variant-options?${params.toString()}`);
      return response.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: productData, isLoading, isFetching } = useQuery({
    queryKey: ["/api/variants", searchQuery, currentPage, sortBy, dynamicFilters, priceSliderRange],
    queryFn: async () => {
      const offset = (currentPage - 1) * itemsPerPage;
      const limit = itemsPerPage;

      let apiSortBy = 'newest';
      if (sortBy === 'price-low-high') {
        apiSortBy = 'price_asc';
      } else if (sortBy === 'price-high-low') {
        apiSortBy = 'price_desc';
      }

      const params = new URLSearchParams({
        search: searchQuery,
        limit: String(limit),
        offset: String(offset),
        sortBy: apiSortBy
      });

      if (priceSliderRange[0] > 0) params.append('minPrice', String(priceSliderRange[0]));
      if (priceSliderRange[1] < 20000) params.append('maxPrice', String(priceSliderRange[1]));

      Object.entries(dynamicFilters.variantOptions).forEach(([optionName, values]) => {
        if (values.length > 0) {
          values.forEach(value => params.append(`variantOption_${optionName}`, value));
        }
      });

      Object.entries(dynamicFilters.productProperties || {}).forEach(([propName, values]) => {
        if (values.length > 0) {
          values.forEach(value => params.append(`productProperty_${propName}`, value));
        }
      });

      const response = await fetch(`/api/variants?${params.toString()}`);
      const data = await response.json();
      return {
        products: data.products || [],
        total: data.total || 0
      };
    },
    staleTime: 60000,
  });

  const products = productData?.products || [];
  const totalProducts = productData?.total ?? 0;
  const displayProducts = products;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearAllFilters = () => {
    setDynamicFilters({
      priceRange: [],
      variantOptions: {},
      productProperties: {},
      productTypes: []
    });
    setPriceSliderRange([0, 20000]);
    setCurrentPage(1);
  };

  const applyMobileFilters = () => {
    setDynamicFilters(tempFilters);
    setPriceSliderRange(tempPriceSliderRange);
    setIsFilterOpen(false);
  };

  const cancelMobileFilters = () => {
    setTempFilters({ ...dynamicFilters });
    setTempPriceSliderRange([...priceSliderRange] as [number, number]);
    setIsFilterOpen(false);
  };

  // Full category page UI for search results
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Desktop Header Bar */}
      <div className="hidden xl:block bg-white/80 backdrop-blur-sm border-b border-slate-200/60 shadow-sm">
        <div className="w-full max-w-[1920px] mx-auto px-6 py-2">
          <nav className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-3">
              <span className="text-slate-900 font-bold tracking-tight text-lg">
                Search Results for &quot;{searchQuery}&quot;
              </span>
              <div className="h-4 w-px bg-slate-200 mx-2" />
              <div className="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-bold rounded-full border border-orange-100 shadow-sm whitespace-nowrap">
                {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImageBg(imageBg === 'white' ? 'black' : 'white')}
                className="bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm hover:shadow transition-all duration-200 rounded-lg px-3 h-8 flex items-center gap-2"
                title={`Switch to ${imageBg === 'white' ? 'black' : 'white'} background`}
              >
                <div className={`w-4 h-4 rounded-full border-2 transition-colors ${imageBg === 'white' ? 'bg-white border-slate-300' : 'bg-black border-slate-700'
                  }`} />
                <span className="text-[12.24px] font-bold text-slate-600 tracking-wide uppercase">Change Background</span>
              </Button>
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="xl:hidden bg-white/95 backdrop-blur-sm border-b border-slate-200/40 w-full">
        <div className="w-full px-2.5 py-2">
          <div className="flex items-center justify-between gap-2 h-8">
            <div className="flex items-center gap-2">
              {/* Mobile Sort Button */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="bg-white border-slate-200 hover:border-orange-300 hover:bg-orange-50 shadow-sm hover:shadow transition-all duration-200 rounded-lg px-3 h-full flex items-center justify-center group">
                    <SortIcon className="w-3.5 h-3.5 mr-1 text-slate-600 group-hover:text-orange-600" />
                    <span className="text-xs font-bold text-slate-700 group-hover:text-orange-600 tracking-wide uppercase">Sort</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto bg-white/95 backdrop-blur-sm rounded-t-2xl">
                  <SheetHeader>
                    <SheetTitle className="text-slate-900 font-bold text-xl tracking-tight">Sort By Price</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-3 pb-6">
                    {[
                      { value: 'price-low-high', label: 'Price: Low to High', icon: '↑' },
                      { value: 'price-high-low', label: 'Price: High to Low', icon: '↓' }
                    ].map(option => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer border-2 transition-all duration-200 ${sortBy === option.value
                          ? 'bg-gradient-to-r from-orange-50 to-orange-100 border-orange-500 shadow-md'
                          : 'bg-white border-slate-200 hover:border-orange-300 hover:bg-orange-50'
                          }`}
                      >
                        <input
                          type="radio"
                          name="sort"
                          checked={sortBy === option.value}
                          onChange={() => setSortBy(option.value)}
                          className="w-5 h-5 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-2xl">{option.icon}</span>
                          <span className={`text-base font-semibold tracking-wide ${sortBy === option.value ? 'text-orange-700' : 'text-slate-700'
                            }`}>{option.label}</span>
                        </div>
                      </label>
                    ))}
                    {sortBy && (
                      <button
                        onClick={() => setSortBy('')}
                        className="w-full mt-2 text-sm text-orange-600 hover:text-orange-700 font-medium py-2 hover:bg-orange-50 rounded-lg transition-colors"
                      >
                        Clear Sort
                      </button>
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              <Button
                variant="outline"
                onClick={() => setImageBg(imageBg === 'white' ? 'black' : 'white')}
                className="bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm hover:shadow transition-all duration-200 rounded-lg px-2.5 h-full flex items-center justify-center group"
                title={`Switch to ${imageBg === 'white' ? 'black' : 'white'} background`}
                aria-label={`Switch to ${imageBg === 'white' ? 'black' : 'white'} background`}
              >
                <div className={`w-4 h-4 rounded-full border-2 transition-colors ${imageBg === 'white' ? 'bg-white border-slate-300' : 'bg-black border-slate-700'
                  }`} />
              </Button>

              <div className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold rounded-full whitespace-nowrap tracking-wider">
                {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
              </div>
            </div>

            <Sheet open={isFilterOpen} onOpenChange={(open) => {
              if (open) {
                setTempFilters({ ...dynamicFilters });
                setTempPriceSliderRange([...priceSliderRange] as [number, number]);
              }
              setIsFilterOpen(open);
            }}>
              <SheetTrigger asChild>
                <Button variant="outline" className="bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm hover:shadow transition-all duration-200 rounded-lg px-3 h-full flex items-center justify-center group" aria-label="Open filters">
                  <Filter className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[90vh] bg-white/95 backdrop-blur-sm rounded-t-2xl flex flex-col">
                <SheetHeader className="flex-shrink-0">
                  <SheetTitle className="text-slate-900 text-[21px] font-bold tracking-tight">Advanced Filters</SheetTitle>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto mt-6 pb-24 -webkit-overflow-scrolling-touch touch-pan-y scroll-smooth" style={{ maxHeight: 'calc(90vh - 120px)' }}>
                  <SearchFilterContent
                    isMobile={true}
                    variantOptions={variantOptions}
                    dynamicFilters={dynamicFilters}
                    setDynamicFilters={setDynamicFilters}
                    tempFilters={tempFilters}
                    setTempFilters={setTempFilters}
                    expandedSections={expandedSections}
                    setExpandedSections={setExpandedSections}
                    showAllOptions={showAllOptions}
                    setShowAllOptions={setShowAllOptions}
                    priceSliderRange={tempPriceSliderRange}
                    setPriceSliderRange={setTempPriceSliderRange}
                  />
                </div>
                <div className="flex-shrink-0 absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm p-4 border-t border-slate-200/60">
                  <div className="flex gap-3 max-w-md mx-auto">
                    <Button variant="outline" className="flex-1 bg-white border-slate-300" onClick={cancelMobileFilters}>
                      Cancel
                    </Button>
                    <Button className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg" onClick={applyMobileFilters}>
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <section className="py-3 xl:py-6">
        <div className="w-full max-w-[1920px] mx-auto px-0 xl:px-6">
          <div className="flex flex-col xl:flex-row gap-0 xl:gap-6">
            {/* Desktop Filters */}
            <aside className="hidden xl:block xl:w-72 xl:flex-shrink-0">
              <div className={`bg-white border border-slate-200 rounded-lg shadow-sm transition-all duration-300 ${sidebarSticky ? 'sticky top-[120px]' : 'relative'
                }`}>
                <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-orange-50 to-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FilterFunnelIcon className="w-5 h-5 text-orange-500" />
                      <h3 className="font-extrabold text-slate-900 text-[16.8px] tracking-tight uppercase">Filters</h3>
                    </div>

                  </div>
                </div>
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-5 custom-scrollbar">
                  <SearchFilterContent
                    variantOptions={variantOptions}
                    dynamicFilters={dynamicFilters}
                    setDynamicFilters={setDynamicFilters}
                    tempFilters={tempFilters}
                    setTempFilters={setTempFilters}
                    expandedSections={expandedSections}
                    setExpandedSections={setExpandedSections}
                    showAllOptions={showAllOptions}
                    setShowAllOptions={setShowAllOptions}
                    priceSliderRange={priceSliderRange}
                    setPriceSliderRange={setPriceSliderRange}
                  />
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1 min-w-0">
              {/* Desktop Sort Section + Active Filter Chips */}
              <div className="hidden xl:block mb-4 px-0 pt-0">
                <div className="flex items-center justify-between">
                  {/* Active Filter Chips */}
                  <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0 mr-4">
                    {(() => {
                      const activeChips: { label: string; filterType: 'variantOptions' | 'productProperties'; optionName: string; value: string }[] = [];
                      const seenValues = new Set<string>();

                      Object.entries(dynamicFilters.variantOptions).forEach(([optionName, values]) => {
                        (values as string[]).forEach(value => {
                          if (!seenValues.has(value)) {
                            seenValues.add(value);
                            activeChips.push({ label: `${value}`, filterType: 'variantOptions', optionName, value });
                          }
                        });
                      });
                      Object.entries(dynamicFilters.productProperties || {}).forEach(([optionName, values]) => {
                        (values as string[]).forEach(value => {
                          if (!seenValues.has(value)) {
                            seenValues.add(value);
                            activeChips.push({ label: `${value}`, filterType: 'productProperties', optionName, value });
                          }
                        });
                      });
                      if (priceSliderRange[0] > 0 || priceSliderRange[1] < 20000) {
                        activeChips.push({ label: `₹${priceSliderRange[0].toLocaleString('en-IN')} - ₹${priceSliderRange[1].toLocaleString('en-IN')}`, filterType: 'variantOptions', optionName: '__price__', value: '__price__' });
                      }
                      if (activeChips.length === 0) return null;
                      return (
                        <>
                          {activeChips.map((chip, idx) => (
                            <span
                              key={`${chip.filterType}-${chip.optionName}-${chip.value}-${idx}`}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-orange-700 text-xs font-semibold rounded-full border border-orange-200 shadow-sm hover:border-orange-300 hover:shadow transition-all duration-200"
                            >
                              {chip.label}
                              <button
                                onClick={() => {
                                  if (chip.optionName === '__price__') {
                                    setPriceSliderRange([0, 20000]);
                                  } else {
                                    setDynamicFilters((prev: typeof dynamicFilters) => {
                                      const newFilters = { ...prev };
                                      const type = chip.filterType;
                                      newFilters[type] = { ...newFilters[type] };
                                      newFilters[type][chip.optionName] = (newFilters[type][chip.optionName] || []).filter((v: string) => v !== chip.value);
                                      if (newFilters[type][chip.optionName].length === 0) {
                                        delete newFilters[type][chip.optionName];
                                      }
                                      return newFilters;
                                    });
                                  }
                                }}
                                className="text-orange-400 hover:text-orange-600 hover:bg-orange-50 rounded-full p-1 transition-colors"
                                aria-label={`Remove ${chip.label} filter`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          <button
                            onClick={clearAllFilters}
                            className="text-xs text-orange-600 hover:text-orange-700 font-bold px-2 py-1 hover:bg-orange-50 rounded-lg transition-colors tracking-wide"
                          >
                            Clear All
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  {/* Sort Controls */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-sm font-bold text-slate-600 tracking-wide uppercase">Sort by:</span>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[220px] bg-white border-slate-200 hover:border-orange-300 focus:border-orange-500 focus:ring-orange-500 shadow-sm hover:shadow transition-all duration-200">
                        <div className="flex items-center gap-2">
                          <SortIcon className="w-4 h-4 text-slate-500" />
                          <SelectValue placeholder="Price order" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200 shadow-lg">
                        <SelectItem
                          value="price-low-high"
                          className="cursor-pointer hover:bg-orange-50 focus:bg-orange-50 focus:text-orange-700"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">↑</span>
                            <span>Price: Low to High</span>
                          </div>
                        </SelectItem>
                        <SelectItem
                          value="price-high-low"
                          className="cursor-pointer hover:bg-orange-50 focus:bg-orange-50 focus:text-orange-700"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">↓</span>
                            <span>Price: High to Low</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {sortBy && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSortBy('')}
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-9"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Show skeleton on initial load with no data */}
              {isLoading && displayProducts.length === 0 ? (
                <div className="grid grid-cols-2 md:border-none md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 xl:gap-8 px-0">
                  {[...Array(9)].map((_, i) => (
                    <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                      <div className="aspect-square bg-slate-200 animate-pulse" />
                      <div className="p-3 space-y-2">
                        <div className="h-4 w-3/4 bg-slate-200 rounded animate-pulse" />
                        <div className="h-3 w-1/2 bg-slate-200 rounded animate-pulse" />
                        <div className="h-5 w-20 bg-slate-200 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Subtle refresh indicator when updating data in background */}
                  {isFetching && displayProducts.length > 0 && (
                    <div className="flex items-center justify-center mb-4">
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 text-orange-600 rounded-full text-sm">
                        <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                        <span>Updating...</span>
                      </div>
                    </div>
                  )}
                  <div className={`grid grid-cols-2 md:border-none md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0 xl:gap-8 px-0 ${isFetching ? 'opacity-70 transition-opacity' : ''}`}>
                    {displayProducts.map((product: any, index: number) => (
                      <ProductCard
                        key={product.variants?.[0]?.id || product.id}
                        product={product}
                        imageBg={imageBg}
                        priority={index < 4}
                      />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="h-9 px-3"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                          const showPage =
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1);

                          const showEllipsis =
                            (page === currentPage - 2 && currentPage > 3) ||
                            (page === currentPage + 2 && currentPage < totalPages - 2);

                          if (showEllipsis) {
                            return <span key={page} className="px-2 text-gray-400">...</span>;
                          }

                          if (!showPage) return null;

                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              aria-label={`Go to page ${page}`}
                              aria-current={currentPage === page ? "page" : undefined}
                              className={`h-9 w-9 rounded-md border text-sm font-medium transition-colors ${currentPage === page
                                ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
                                : "border-gray-300 bg-white hover:bg-gray-50"
                                }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="h-9 px-3"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {/* Results Info */}
                  {totalProducts > 0 && (
                    <div className="mt-4 text-center text-sm text-gray-600 font-medium tracking-wide">
                      Showing {startIndex + 1}-{endIndex} of {totalProducts} products
                      {totalPages > 1 && (
                        <span className="ml-2 text-orange-600 font-bold">
                          • Page {currentPage} of {totalPages}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Show "No Products Found" only when query has completed and no products */}
              {displayProducts.length === 0 && !isLoading && !isFetching && productData && totalProducts === 0 && (
                <div className="text-center py-20">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                      <div className="w-10 h-10 border-2 border-white rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">0</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-extrabold text-slate-900 mb-4 tracking-tight">No Products Found</h3>
                    <p className="text-slate-600 mb-6 font-medium tracking-wide">
                      We couldn&apos;t find any products matching your criteria. Try adjusting your filters or search term.
                    </p>
                    <Button
                      onClick={clearAllFilters}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-xl font-bold tracking-wide shadow-lg shadow-orange-500/25"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Filter content component for search page
const SearchFilterContent = ({
  isMobile = false,
  variantOptions,
  dynamicFilters,
  setDynamicFilters,
  tempFilters,
  setTempFilters,
  expandedSections,
  setExpandedSections,
  showAllOptions,
  setShowAllOptions,
  priceSliderRange,
  setPriceSliderRange
}: {
  isMobile?: boolean;
  variantOptions: any;
  dynamicFilters: any;
  setDynamicFilters: React.Dispatch<React.SetStateAction<any>>;
  tempFilters: any;
  setTempFilters: React.Dispatch<React.SetStateAction<any>>;
  expandedSections: Record<string, boolean>;
  setExpandedSections: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  showAllOptions: Record<string, boolean>;
  setShowAllOptions: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  priceSliderRange: [number, number];
  setPriceSliderRange: (val: [number, number]) => void;
}) => {
  const availableOptions = variantOptions?.options || {};
  const currentFilters = isMobile ? tempFilters : dynamicFilters;
  const setCurrentFilters = isMobile ? setTempFilters : setDynamicFilters;

  // Separate variant options from product properties
  const variantOptionKeys = ['Color Family', 'Finish', 'Look/Pattern', 'Size', 'Thickness'];
  const productPropertyKeys = ['Application', 'Application grade', 'Laminate Type', 'Product Type'];

  const variantOptionsFilters = Object.entries(availableOptions).filter(([key]) =>
    variantOptionKeys.includes(key)
  );

  const productPropertiesFilters = Object.entries(availableOptions).filter(([key]) =>
    productPropertyKeys.includes(key)
  );

  const handleOptionToggle = (optionName: string, value: string, checked: boolean, isProductProperty: boolean = false) => {
    setCurrentFilters((prev: any) => {
      const newFilters = { ...prev };
      const filterType = isProductProperty ? 'productProperties' : 'variantOptions';

      if (!newFilters[filterType]) {
        newFilters[filterType] = {};
      }
      if (!newFilters[filterType][optionName]) {
        newFilters[filterType][optionName] = [];
      }

      if (checked) {
        newFilters[filterType][optionName] = [...newFilters[filterType][optionName], value];
      } else {
        newFilters[filterType][optionName] = newFilters[filterType][optionName].filter((v: string) => v !== value);
      }
      return newFilters;
    });
  };

  const renderFilterSection = (entries: [string, any][], isProductProperty: boolean = false) => {
    return entries.map(([optionName, values]: [string, any]) => {
      const optionKey = optionName.toLowerCase().replace(/\s+/g, '-');
      const isExpanded = expandedSections[optionKey] ?? true;
      const showAll = showAllOptions[optionKey] ?? false;
      const displayValues = showAll ? values : values.slice(0, 5);
      const hasMore = values.length > 5;
      const filterType = isProductProperty ? 'productProperties' : 'variantOptions';
      const selectedCount = currentFilters[filterType]?.[optionName]?.length || 0;

      return (
        <div key={optionName} className="border-b border-slate-200 pb-4 last:border-b-0">
          <button
            onClick={() => setExpandedSections(prev => ({ ...prev, [optionKey]: !isExpanded }))}
            className="flex items-center justify-between w-full text-left py-2 px-3 rounded-lg hover:bg-slate-50 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <h4 className="font-bold text-slate-900 text-[14.7px] tracking-wide uppercase">{optionName}</h4>
              <span className="text-[12.6px] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full font-semibold">({values.length})</span>
              {selectedCount > 0 && (
                <span className="text-[12.6px] text-white bg-orange-500 px-2 py-0.5 rounded-full font-bold">
                  {selectedCount}
                </span>
              )}
            </div>
            <ChevronDownIcon className={`w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-all ${isExpanded ? 'rotate-180' : ''}`} />
          </button>

          {isExpanded && (
            <div className="mt-2">
              {displayValues.map((item: any) => {
                const isChecked = currentFilters[filterType]?.[optionName]?.includes(item.value) || false;
                return (
                  <label
                    key={item.value}
                    className="flex items-center justify-between px-2.5 py-1 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => handleOptionToggle(optionName, item.value, checked as boolean, isProductProperty)}
                      />
                      <span className="text-[14.7px] font-semibold text-slate-700 group-hover:text-slate-900 tracking-wide">{item.value}</span>
                    </div>
                    <div className="text-[12.6px] px-2.5 py-1 rounded-full font-bold bg-slate-100 text-slate-600 group-hover:bg-orange-100 group-hover:text-orange-700 transition-colors">
                      {item.count}
                    </div>
                  </label>
                );
              })}
              {hasMore && (
                <button
                  onClick={() => setShowAllOptions(prev => ({ ...prev, [optionKey]: !showAll }))}
                  className="text-[14.7px] text-orange-600 hover:text-orange-700 font-bold mt-2 px-2 py-1 hover:bg-orange-50 rounded transition-colors tracking-wide"
                >
                  {showAll ? '− Show less' : `+ Show ${values.length - 5} more`}
                </button>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={`space-y-4 ${isMobile ? 'min-h-0' : ''}`}>
      {/* Price Range Slider */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-2 mb-3">
          <h4 className="font-extrabold text-slate-900 text-[14.7px] tracking-wider uppercase">PRICE</h4>
        </div>
        <SearchPriceSlider value={priceSliderRange} onValueCommit={setPriceSliderRange} />
      </div>

      {variantOptionsFilters.length > 0 && renderFilterSection(variantOptionsFilters, false)}
      {productPropertiesFilters.length > 0 && renderFilterSection(productPropertiesFilters, true)}
    </div>
  );
};

// Price slider component for search page
const SearchPriceSlider = ({
  value,
  onValueCommit,
  min = 0,
  max = 20000
}: {
  value: [number, number],
  onValueCommit: (val: [number, number]) => void,
  min?: number,
  max?: number
}) => {
  const [localValue, setLocalValue] = useState<[number, number]>(value);

  // Sync local value when parent value changes (e.g. on clear all)
  useEffect(() => {
    setLocalValue(value);
  }, [value[0], value[1]]);

  return (
    <div className="px-2">
      <Slider
        min={min}
        max={max}
        step={1}
        value={localValue}
        onValueChange={(val) => setLocalValue(val as [number, number])}
        onValueCommit={(val) => onValueCommit(val as [number, number])}
        className="mb-4"
      />
      <div className="text-center text-[14.7px] font-bold text-slate-700 tracking-wide">
        ₹{localValue[0].toLocaleString('en-IN')} - ₹{localValue[1].toLocaleString('en-IN')}
      </div>
    </div>
  );
};

interface HomePageClientProps {
  initialConfig: HomePageConfig | null;
  initialSearchQuery?: string;
  carouselConfig?: {
    slides: Array<{
      id: string;
      color_code: string;
      image: string;
      line1: string;
      line2: string;
      line3: string;
      description: string;
      below_handle: string;
      link_text: string;
      badge?: string;
    }>;
  };
}

export function HomePageClient({ carouselConfig, initialSearchQuery = '' }: HomePageClientProps) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams?.get('search') ?? initialSearchQuery;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [nextSlideIndex, setNextSlideIndex] = useState<number | null>(null);
  const [autoAdvanceKey, setAutoAdvanceKey] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isTransitioningRef = useRef(false);

  const slides = carouselConfig?.slides || [
    {
      id: "1",
      color_code: "#F5F5DC",
      image: "https://pub-ea8ea29f2c324fe5aaa533e352a67096.r2.dev/Carousel%20Images/Interior%20wall%20design%20is%20one%20of%20the%20most%20powerful%E2%80%A6.jpeg",
      line1: "Transform Your Space",
      line2: "with",
      line3: "Premium Materials",
      description: "Discover our curated collection of Plywood, Laminates, Hardware, Veneers & Decoratives. Trusted by homeowners, architects, and interior designers across the nation.",
      below_handle: "/laminates",
      link_text: "Explore Laminates",
      badge: "Your One-Stop Interior Materials Destination"
    }
  ];

  // Preload next slide images for instant transitions
  useEffect(() => {
    slides.forEach((slide) => {
      const img = new window.Image();
      img.src = slide.image;
    });
  }, [slides]);

  // Auto-advance carousel - smooth crossfade, no white flash
  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      // Guard: skip if a transition is already in progress
      if (isTransitioningRef.current) return;
      const next = (currentSlide + 1) % slides.length;
      setNextSlideIndex(next);
      setIsTransitioning(true);
      isTransitioningRef.current = true;
      setTimeout(() => {
        setCurrentSlide(next);
        setNextSlideIndex(null);
        setIsTransitioning(false);
        isTransitioningRef.current = false;
      }, 700);
    }, 10000);

    return () => clearInterval(interval);
  }, [slides.length, autoAdvanceKey, currentSlide]);

  const transitionToSlide = (index: number) => {
    if (isTransitioningRef.current || index === currentSlide) return;
    setNextSlideIndex(index);
    setIsTransitioning(true);
    isTransitioningRef.current = true;
    setTimeout(() => {
      setCurrentSlide(index);
      setNextSlideIndex(null);
      setIsTransitioning(false);
      isTransitioningRef.current = false;
    }, 700);
    setAutoAdvanceKey(prev => prev + 1);
  };

  const nextSlide = () => {
    transitionToSlide((currentSlide + 1) % slides.length);
  };

  const prevSlide = () => {
    transitionToSlide((currentSlide - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    transitionToSlide(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) nextSlide();
    if (distance < -50) prevSlide();
    setTouchStart(0);
    setTouchEnd(0);
  };

  // If there's a search query, show search results
  if (searchQuery) {
    return <SearchCategoryPage searchQuery={searchQuery} />;
  }

  return (
    <div>
      {/* Hero Section - Split Layout Carousel */}
      <section className="relative w-full overflow-hidden">
        {/* Mobile Carousel Design - Crossfade, no flash */}
        <div
          className="md:!hidden relative h-[50vh]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Stacked slide layers for crossfade */}
          {slides.map((slide, index) => {
            const isActive = index === currentSlide;
            const isNext = index === nextSlideIndex && isTransitioning;
            return (
              <div
                key={slide.id}
                className="absolute inset-0"
                style={{
                  opacity: isNext ? 1 : (isActive && !isTransitioning ? 1 : (isActive && isTransitioning ? 0 : 0)),
                  zIndex: isNext ? 2 : (isActive ? 1 : 0),
                  transition: 'opacity 0.7s ease-in-out',
                }}
              >
                <Image
                  src={slide.image}
                  alt={`${slide.line1} ${slide.line2} ${slide.line3}`}
                  fill
                  sizes="100vw"
                  priority={index === 0}
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>
            );
          })}

          <div className="relative z-10 h-full flex flex-col justify-end px-6 pb-20">
            <div key={currentSlide} className="max-w-2xl">
              <style jsx global>{`
                @keyframes slideUp {
                  from {
                    opacity: 0;
                    transform: translateY(30px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
                .animate-slide-up {
                  animation: slideUp 0.6s ease-out forwards;
                }
              `}</style>
              {slides[currentSlide].badge && (
                <div className="mb-4 animate-slide-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
                  <span
                    className="inline-block px-3 py-1 text-[10px] rounded-full bg-[#F9A63A]/20 text-[#FDE68A] font-medium shadow-sm backdrop-blur-sm border border-[#F9A63A]/30"
                  >
                    {slides[currentSlide].badge}
                  </span>
                </div>
              )}

              <p
                className="text-[24px] font-bold text-white mb-4 leading-tight animate-slide-up"
                style={{ animationDelay: '0.2s', opacity: 0 }}
              >
                <span className="block font-sans font-extrabold">{slides[currentSlide].line1}</span>
                <span className="block font-sans font-extrabold">{slides[currentSlide].line2}</span>
                <span className="block font-sans font-extrabold text-amber-300">{slides[currentSlide].line3}</span>
              </p>
              <p
                className="text-white/90 text-[11px] mb-6 leading-relaxed animate-slide-up font-extrabold"
                style={{ animationDelay: '0.3s', opacity: 0 }}
              >
                {slides[currentSlide].description}
              </p>

              <Link
                href={slides[currentSlide].below_handle}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg]:size-4 [&amp;_svg]:shrink-0 py-2 h-10 px-6 md:h-12 md:px-8 bg-amber-500 hover:bg-amber-600 text-black md:text-base font-extrabold rounded-xl shadow-xl shadow-amber-900/30 transition-all duration-300 hover:scale-105 active:scale-95 border-none animate-slide-up"
                style={{ animationDelay: '0.4s', opacity: 0 }}
              >
                {slides[currentSlide].link_text}
                <ChevronRightIcon className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="relative flex items-center justify-center p-1"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span className={`block h-2 rounded-full transition-all ${currentSlide === index
                  ? 'w-8 bg-amber-500'
                  : 'w-2 bg-white/50 hover:bg-white/80'
                  }`} />
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Split Layout - Crossfade between slides */}
        <div className="!hidden md:!block relative min-h-[600px] lg:min-h-[530px]">
          {slides.map((slide, index) => {
            const isActive = index === currentSlide;
            const isNext = index === nextSlideIndex && isTransitioning;
            return (
              <div
                key={slide.id}
                className="absolute inset-0 grid grid-cols-2"
                style={{
                  opacity: isNext ? 1 : (isActive && !isTransitioning ? 1 : (isActive && isTransitioning ? 0 : 0)),
                  zIndex: isNext ? 2 : (isActive ? 1 : 0),
                  transition: 'opacity 0.7s ease-in-out',
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
              >
                <div className="flex flex-col justify-center px-16 lg:px-24 py-12" style={{ backgroundColor: slide.color_code }}>
                  {(isActive || isNext) && (
                    <div key={`content-${index}`} className="flex flex-col justify-center">
                      {slide.badge && (
                        <div className="mb-3 animate-slide-up" style={{ animationDelay: '0.1s', opacity: 0 }}>
                          <span className="inline-block px-3 py-1.5 bg-foreground/10 text-foreground text-xs font-semibold rounded-full lg:hidden">
                            {slide.badge}
                          </span>
                        </div>
                      )}
                      <h1
                        className="font-serif text-5xl lg:!text-[60px] font-semibold leading-[1.1] text-foreground mb-5 max-w-lg animate-slide-up"
                        style={{ animationDelay: '0.2s', opacity: 0 }}
                      >
                        {slide.line1} {slide.line2} {slide.line3}
                      </h1>
                      <p
                        className="text-foreground/80 text-lg max-w-md mb-8 leading-relaxed font-light animate-slide-up"
                        style={{ animationDelay: '0.3s', opacity: 0 }}
                      >
                        {slide.description}
                      </p>
                      <Link
                        href={slide.below_handle}
                        className="inline-flex items-center justify-center gap-2 bg-foreground text-background px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-foreground/90 transition-colors w-fit animate-slide-up"
                        style={{ animationDelay: '0.4s', opacity: 0 }}
                      >
                        {slide.link_text}
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <Image src={slide.image} alt={`${slide.line1} ${slide.line2} ${slide.line3}`} fill sizes="50vw" priority={index === 0} className="object-cover" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Navigation */}
        <button
          onClick={prevSlide}
          aria-label="Previous slide"
          className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 hover:bg-white items-center justify-center transition-all shadow-lg"
        >
          <ArrowLeftIcon className="w-4 h-4 text-foreground" />
        </button>

        <button
          onClick={nextSlide}
          aria-label="Next slide"
          className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 hover:bg-white items-center justify-center transition-all shadow-lg"
        >
          <ArrowRightIcon className="w-4 h-4 text-foreground" />
        </button>

        {/* Desktop Dots */}
        <div className="hidden md:flex absolute bottom-5 left-1/2 -translate-x-1/2 z-20 gap-1.5">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="relative flex items-center justify-center p-0"
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className={`block w-2.5 h-2.5 rounded-full transition-colors ${currentSlide === index ? 'bg-foreground' : 'bg-foreground/25'}`} />
            </button>
          ))}
        </div>
      </section>

    </div >
  );
}
